import { useState, useCallback, createContext, useContext } from 'react'

const CONFIG_KEY = 'appconfig'
const DEFAULT_OVERRIDE_DATE_KEY = 'override-date-key'
const DEFAULT_CONFIG = {
  flagBounds: {
    marketRent: { upper: 50, lower: -50 },
    actualRent: { upper: 30, lower: -30 },
    competitorDiff: { upper: 50, lower: -50 },
    groupOutlier: { upper: 2.5, lower: -2.5 },
  },
  sqmUnitToggleChoice: 'unit',
  priceBounds: {
    bound: { upper: 30, lower: -30 },
    actionDeploy: 'popup',
    actionAccept: 'popup',
    actionOverride: 'popup',
  },
  grouping: {
    unitType: 'grouped',
    amenities: 'grouped',
  },
  siteCategories: {
    lfl: [],
    expanding: [],
    new: [],
  },
}

function isValidObject(obj, reference) {
  if (!obj || typeof obj !== 'object') return false
  for (const key of Object.keys(reference)) {
    if (!(key in obj)) return false
    if (typeof reference[key] === 'object' && reference[key] !== null && !Array.isArray(reference[key])) {
      if (!isValidObject(obj[key], reference[key])) return false
    }
  }
  return true
}

function fixObjectWithDefault(obj, defaultObj) {
  const result = { ...defaultObj }
  if (!obj || typeof obj !== 'object') return result
  for (const key of Object.keys(defaultObj)) {
    if (key in obj) {
      if (typeof defaultObj[key] === 'object' && defaultObj[key] !== null && !Array.isArray(defaultObj[key])) {
        result[key] = fixObjectWithDefault(obj[key], defaultObj[key])
      } else {
        result[key] = obj[key]
      }
    }
  }
  return result
}

function loadConfig() {
  try {
    const stored = localStorage.getItem(CONFIG_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      if (isValidObject(parsed, DEFAULT_CONFIG)) return parsed
      return fixObjectWithDefault(parsed, DEFAULT_CONFIG)
    }
  } catch { /* ignore */ }
  return structuredClone(DEFAULT_CONFIG)
}

function storeConfig(config) {
  localStorage.setItem(CONFIG_KEY, JSON.stringify(config))
}

const ConfigContext = createContext()

export function ConfigProvider({ children }) {
  const value = useConfigInternal()
  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>
}

function useConfigInternal() {
  const [config, setConfig] = useState(loadConfig)

  const persistAndSet = useCallback((updater) => {
    setConfig(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      storeConfig(next)
      return next
    })
  }, [])

  const sqmUnitToggle = config.sqmUnitToggleChoice

  const toggleUnitsSQM = useCallback(() => {
    persistAndSet(prev => ({
      ...prev,
      sqmUnitToggleChoice: prev.sqmUnitToggleChoice === 'sqm' ? 'unit' : 'sqm',
    }))
  }, [persistAndSet])

  const updateFlagBound = useCallback((key, value) => {
    persistAndSet(prev => ({
      ...prev,
      flagBounds: { ...prev.flagBounds, [key]: value },
    }))
  }, [persistAndSet])

  const updatePriceBounds = useCallback((bound, actionDeploy, actionAccept, actionOverride) => {
    persistAndSet(prev => ({
      ...prev,
      priceBounds: { bound, actionDeploy, actionAccept, actionOverride },
    }))
  }, [persistAndSet])

  const grouping = config.grouping || DEFAULT_CONFIG.grouping

  const updateGrouping = useCallback((key, value) => {
    persistAndSet(prev => ({
      ...prev,
      grouping: { ...(prev.grouping || DEFAULT_CONFIG.grouping), [key]: value },
    }))
  }, [persistAndSet])

  const siteCategories = config.siteCategories || DEFAULT_CONFIG.siteCategories

  const updateSiteCategories = useCallback((categories) => {
    persistAndSet(prev => ({
      ...prev,
      siteCategories: { ...DEFAULT_CONFIG.siteCategories, ...categories },
    }))
  }, [persistAndSet])

  const setDefaultOverrideDate = useCallback((dateMs) => {
    localStorage.setItem(DEFAULT_OVERRIDE_DATE_KEY, String(dateMs))
  }, [])

  const getDefaultOverrideDate = useCallback(() => {
    const stored = localStorage.getItem(DEFAULT_OVERRIDE_DATE_KEY)
    if (stored) {
      const ms = parseInt(stored, 10)
      if (!isNaN(ms) && ms > Date.now()) return ms
    }
    return null
  }, [])

  return {
    config,
    flagBounds: config.flagBounds,
    priceBounds: config.priceBounds,
    sqmUnitToggle,
    toggleUnitsSQM,
    updateFlagBound,
    updatePriceBounds,
    setDefaultOverrideDate,
    getDefaultOverrideDate,
    grouping,
    updateGrouping,
    siteCategories,
    updateSiteCategories,
  }
}

export function useConfig() {
  const ctx = useContext(ConfigContext)
  if (!ctx) throw new Error('useConfig must be used within ConfigProvider')
  return ctx
}
